/**
 * Login Page
 * Teacher/admin login
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { loginSchema } from '@/utils/validation';
import type { z } from 'zod';

type LoginFormData = z.infer<typeof loginSchema>;

type ViewMode = 'login' | 'forgotPassword' | 'resetPassword';

export const LoginPage = () => {
  const { login, forgotPassword, resetPassword } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('login');
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof LoginFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof LoginFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await login(formData);
      
      if (response.success) {
        showNotification('success', 'Login successful!');
        navigate('/dashboard');
      } else {
        if (import.meta.env.DEV) {
          console.log('Login failed, checking error type:', response.error);
        }
        
        // Check if error is related to unverified email
        const errorStr = response.error?.toLowerCase() || '';
        if (errorStr.includes('usernotconfirmed') || 
            errorStr.includes('not confirmed') ||
            errorStr.includes('not verified')) {
          if (import.meta.env.DEV) {
            console.log('Unverified user detected, redirecting to verification...');
          }
          showNotification('info', 'Please verify your email address first.');
          // Redirect to verification page immediately
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
          return; // Exit early
        }
        // Check if error is related to no active license
        else if (errorStr.includes('license')) {
          showNotification('error', 'No active school license found. Please purchase a license to continue.');
          // Redirect to pricing page after 2 seconds
          setTimeout(() => {
            window.location.href = 'https://spelltales.co.uk/school-pricing.html';
          }, 2000);
        } else {
          showNotification('error', response.error || 'Login failed');
        }
      }
    } catch (error) {
      console.error('Login exception:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      showNotification('error', 'Please enter your email address');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await forgotPassword(formData.email);
      
      if (response.success) {
        showNotification('success', 'Verification code sent to your email');
        setViewMode('resetPassword');
      } else {
        showNotification('error', response.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode || !newPassword) {
      showNotification('error', 'Please fill in all fields');
      return;
    }

    // Validate password requirements
    if (newPassword.length < 8) {
      showNotification('error', 'Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one number');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await resetPassword(formData.email, verificationCode, newPassword);
      
      if (response.success) {
        showNotification('success', 'Password reset successfully! You can now sign in.');
        setViewMode('login');
        setFormData({ ...formData, password: '' });
        setVerificationCode('');
        setNewPassword('');
      } else {
        showNotification('error', response.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/admin/icon.png" alt="SpellTales" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
            SpellTales School Admin
          </h1>
          <p className="text-slate-600">Sign in to manage your school</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
          {viewMode === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
                placeholder="teacher@school.edu"
                required
                autoComplete="email"
              />

              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={errors.password}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-slate-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => setViewMode('forgotPassword')}
                  className="text-emerald-600 hover:text-emerald-700 font-semibold"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Sign In
              </Button>
            </form>
          )}

          {viewMode === 'forgotPassword' && (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Set Your Password</h2>
                <p className="text-sm text-slate-600">
                  Enter your email and we'll send you a verification code to set your password
                </p>
              </div>

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
                placeholder="teacher@school.edu"
                required
                autoComplete="email"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Send Verification Code
              </Button>

              <button
                type="button"
                onClick={() => setViewMode('login')}
                className="w-full text-center text-sm text-slate-600 hover:text-slate-900"
              >
                Back to Sign In
              </button>
            </form>
          )}

          {viewMode === 'resetPassword' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Create Your Password</h2>
                <p className="text-sm text-slate-600">
                  Enter the verification code sent to {formData.email}
                </p>
              </div>

              <Input
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                autoComplete="off"
              />

              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />

              <div className="text-xs text-slate-600 space-y-1">
                <p className="font-semibold">Password must contain:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                </ul>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Reset Password
              </Button>

              <button
                type="button"
                onClick={() => setViewMode('login')}
                className="w-full text-center text-sm text-slate-600 hover:text-slate-900"
              >
                Back to Sign In
              </button>
            </form>
          )}

          {/* Register Link */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-600">
              First teacher at your school?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                Create Account
              </button>
            </p>
          </div>

          {/* Purchase License Link */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-center text-sm text-slate-600 mb-3">
              Don't have a school license?
            </p>
            <a
              href="https://spelltales.co.uk/school-pricing.html"
              className="block w-full text-center px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-xl transition-colors"
            >
              Purchase a License
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p>© 2026 SpellTales. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
